// ── 매출 관리 데이터 로더 훅 ──────────────────────────────────────────────────
// useRevenue에서 분리된 탭별 데이터 로드 함수 모음
// apiBase를 주입받아 파인그룹·DOT·LNJ 등 모든 회사에서 공통 사용
import { useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';

/**
 * 매출 관리 페이지의 모든 데이터 로더를 생성하는 커스텀 훅
 * @param {{
 *   apiBase,             — API 기본 경로 (예: '/api/admin/revenue', '/api/admin/revenue/dot')
 *   filterStart, filterEnd,
 *   setSalesUsers, setTaxInvoices, setPayments, setInvoices,
 *   setPhoneApps, setOthers, setCardApps, setCardBatches, setTaxBatches,
 * }} options
 * @returns 로더 함수 객체
 */
export function useRevenueLoaders({
  apiBase,
  filterStart, filterEnd,
  setSalesUsers, setTaxInvoices, setPayments, setInvoices,
  setPhoneApps, setOthers, setCardApps, setCardBatches, setTaxBatches,
}) {
  /** 공통 날짜 필터 쿼리 파라미터 생성 */
  function buildParams() {
    const params = new URLSearchParams();
    if (filterStart) params.set('start_date', filterStart);
    if (filterEnd)   params.set('end_date',   filterEnd);
    return params;
  }

  /** 마케터 목록 조회 (필터 없음 — 공통 드롭다운용) */
  const loadSalesUsers = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/sales-users`);
    if (r.data?.success) setSalesUsers(r.data.data || []);
  }, [apiBase, setSalesUsers]);

  /** 세금계산서 목록 조회 (입금 연결 팝업용 + 세금계산서 탭) */
  const loadTaxInvoices = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/tax-invoices?${buildParams()}`);
    if (r.data?.success) setTaxInvoices(r.data.data || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, filterStart, filterEnd, setTaxInvoices]);

  /** 입금 목록 조회 */
  const loadPayments = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/payments?${buildParams()}`);
    if (r.data?.success) setPayments(r.data.data || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, filterStart, filterEnd, setPayments]);

  /** 세금계산서 탭용 목록 조회 (loadTaxInvoices와 동일 엔드포인트, invoices 상태에 저장) */
  const loadInvoices = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/tax-invoices?${buildParams()}`);
    if (r.data?.success) setInvoices(r.data.data || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, filterStart, filterEnd, setInvoices]);

  /** 전화승인 목록 조회 */
  const loadPhoneApps = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/card-phone-approvals?${buildParams()}`);
    if (r.data?.success) setPhoneApps(r.data.data || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, filterStart, filterEnd, setPhoneApps]);

  /** 기타 수입 목록 조회 */
  const loadOthers = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/others?${buildParams()}`);
    if (r.data?.success) setOthers(r.data.data || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, filterStart, filterEnd, setOthers]);

  /** 카드승인 목록 조회 */
  const loadCardApps = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/card-approvals?${buildParams()}`);
    if (r.data?.success) setCardApps(r.data.data || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, filterStart, filterEnd, setCardApps]);

  /** 카드 배치(일괄업로드) 목록 조회 (필터 없음) */
  const loadCardBatches = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/card-batches`);
    if (r.data?.success) setCardBatches(r.data.data || []);
  }, [apiBase, setCardBatches]);

  /** 세금계산서 배치 목록 조회 (필터 없음) */
  const loadTaxBatches = useCallback(async () => {
    const r = await apiClient.get(`${apiBase}/tax-invoice-batches`);
    if (r.data?.success) setTaxBatches(r.data.data || []);
  }, [apiBase, setTaxBatches]);

  return {
    loadSalesUsers, loadTaxInvoices, loadPayments, loadInvoices,
    loadPhoneApps, loadOthers, loadCardApps, loadCardBatches, loadTaxBatches,
  };
}
